import React, { ChangeEvent, MouseEvent, ReactNode, useEffect, useRef } from 'react';
import { bemHelper } from '../../../helpers';
import setFocus from '../../../helpers/dom/setFocus';
import Control from '../../Control/Control';

export type RadioInputElement = {
  onBlur?: (e: ChangeEvent<HTMLInputElement>) => void;
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
  onClick?: (e: MouseEvent<HTMLInputElement>) => void;
  onFocus?: (e: ChangeEvent<HTMLInputElement>) => void;
  name?: string;
};

export interface RadioOptionProps {
  id: string;
  name?: string;
  size?: 'full' | 'large' | 'half' | 'quarter' | 'auto';
  label?: ReactNode;
  value?: string | number;
  isCell?: boolean;
  isFieldless?: boolean;
  isChecked?: boolean;
  isDisabled?: boolean;
  isInvalid?: boolean;
  isInline?: boolean;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onClick?: (e: React.MouseEvent<HTMLInputElement>) => void;
  onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void;
  autoFocus?: boolean;
  dataPrivate?: boolean;

  /** @ignore */
  input: RadioInputElement;
  /** @ignore */
  ref?: React.RefObject<any>;
}

function RadioOption(props: RadioOptionProps) {
  const {
    id,
    name,
    size,
    value,
    input,
    label,
    isCell,
    isFieldless,
    isChecked,
    isDisabled,
    isInvalid,
    isInline,
    onFocus,
    onChange,
    onBlur,
    onClick,
    autoFocus,
    dataPrivate,
  } = props;

  function defineClassName() {
    const b = bemHelper();

    return b('ink-control--radio', {
      cell: isCell,
      fieldless: isFieldless,
    });
  }

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    if (input.onChange) {
      input.onChange(e);
    }
    if (onChange) {
      onChange(e);
    }
  }

  function handleBlur(e: React.FocusEvent<HTMLInputElement>) {
    if (input.onBlur) {
      input.onBlur(e);
    }
    if (onBlur) {
      onBlur(e);
    }
  }

  function handleFocus(e: React.FocusEvent<HTMLInputElement>) {
    if (input.onFocus) {
      input.onFocus(e);
    }
    if (onFocus) {
      onFocus(e);
    }
  }

  function handleClick(e: React.MouseEvent<HTMLInputElement>) {
    if (input.onClick) {
      input.onClick(e);
    }
    if (onClick) {
      onClick(e);
    }
  }

  // eslint-disable-next-line ssr-friendly/no-dom-globals-in-react-fc
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (props.autoFocus) setFocus(inputRef);
  }, [inputRef, props.autoFocus]);

  return (
    <Control
      size={size}
      inputId={name}
      isCell={isCell}
      isInline={isInline}
      isInvalid={isInvalid}
      isDisabled={isDisabled}
      isRadio
      dataPrivate={dataPrivate}
    >
      <div id={`radio-option-${id}`} className={defineClassName()}>
        <input
          id={id}
          ref={inputRef}
          className="ink-control--radio--entry"
          type="radio"
          name={name}
          value={value}
          checked={isChecked}
          onFocus={handleFocus}
          onChange={handleChange}
          onBlur={handleBlur}
          onClick={handleClick}
          disabled={isDisabled}
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus={autoFocus}
          aria-checked={isChecked}
        />
        <label htmlFor={id} className="ink-control--radio--appearance" />
        {Boolean(label) && (
          <label htmlFor={id} className="ink-control--radio--label">
            {label}
          </label>
        )}
      </div>
    </Control>
  );
}

RadioOption.defaultProps = {
  size: 'large',
  label: '',
  value: '',
  isCell: false,
  isFieldless: false,
  isChecked: false,
  isDisabled: false,
  isInvalid: false,
  isInline: false,
  onFocus: undefined,
  onChange: undefined,
  onBlur: undefined,
  onClick: undefined,
  input: {},
  autoFocus: false,
  dataPrivate: undefined,
};

export default RadioOption;
